import { h, Component, Event, EventEmitter, Fragment, State, Prop, Host } from '@stencil/core';
import state from '../../state';
import {
  getViewportValue,
  transitionScreen,
  updateStoredField,
  getStoredFormData,
  clearFormStorage,
} from '../../utils';
import { detectUserCountry } from '../../../../utils/detectUserCountry';
import countries from '../../../../assets/countries';
import { SIGNUP_URL } from '../../constants';

import type { SMSContent, SubmitEventArg } from '../../types';

const getFlag = (langcode: string) => {
  const first = langcode.charCodeAt(0) + 127397;
  const second = langcode.charCodeAt(1) + 127397;
  const flag = `&#${first};&#${second};`;
  return flag;
};

const options = countries.map(country => {
  const flag = getFlag(country.code) + '&nbsp;';
  return {
    value: country.code,
    label: <div class="flag-container" innerHTML={flag} />,
    html: () => (
      <Fragment>
        <div style={{ lineHeight: '1' }} innerHTML={flag} />
        <div>{country.name}</div>
        <div style={{ marginLeft: '15px' }}>{country.dial_code}</div>
      </Fragment>
    ),
  };
});

@Component({
  tag: 'om-sms-screen',
  styleUrls: ['./screens.css', './sms-screen.css'],
  scoped: true,
})
export class SMSScreen {
  @Prop() screenValues: SMSContent;
  @Prop() handleSubmit: (d: SubmitEventArg) => void;

  @State() countryCode: string = detectUserCountry() || 'GB';
  @State() phoneNumberErrorMsg: string;

  @Event() layoutContainerClicked: EventEmitter;

  submitEvent: EventEmitter<SubmitEventArg>;
  numberInput: HTMLInputElement;

  phoneNumberValue: string = '';
  private hasInitialized = false;

  private submitEventHandler(identifier?: string, emailAddress?: string) {
    if (state.mode !== 'live') {
      return;
    }
    this.handleSubmit({ channel: 'phone', ...(identifier && { identifier }), ...(emailAddress && { emailAddress }) });
  }

  private phoneNumberInput() {
    const { placeholder } = getViewportValue(this.screenValues.input);
    const containerRefKey = 'omOverlayPhoneInput';
    return (
      <om-clickable-element
        containerRef={(el: HTMLElement) => (state.dropdownContainerRefs[containerRefKey] = el)}
        element={{ field: 'input' }}
        class="input-container phone"
      >
        <div class="phone-country-dropdown">
          <om-dropdown-input
            id="om-phone-acquisition-country"
            initialValue={this.countryCode}
            options={options}
            handleSelect={(option: { value: string }) => {
              this.countryCode = option.value;

              if (state.mode !== 'preview' && state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
                updateStoredField('sms', 'country_code', option.value);
              }
            }}
            containerRefKey={containerRefKey}
          />
        </div>
        <input
          tabindex={0}
          aria-label="Phone number"
          {...(state.mode === 'preview' ? { inert: true } : { autoFocus: true })}
          ref={el => (this.numberInput = el)}
          id="om_overlay_phone_number_input"
          type="text"
          name="phone_number"
          placeholder={placeholder}
          class="input phone-number-input"
          {...(this.phoneNumberValue && { value: this.phoneNumberValue })}
          onKeyDown={e => {
            if (
              e?.key === 'Backspace' ||
              e?.key === 'Delete' ||
              e?.key === 'ArrowRight' ||
              e?.key === 'ArrowLeft' ||
              e?.key === 'ArrowUp' ||
              e?.key === 'ArrowDown' ||
              e?.key === 'Tab' ||
              !isNaN(parseInt(e?.key))
            ) {
              return true;
            } else {
              e.preventDefault();
              return false;
            }
          }}
          onChange={e => {
            const input = e.target as HTMLInputElement;
            this.phoneNumberValue = input.value;
          }}
          onBlur={e => {
            if (state.mode !== 'preview' && state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
              const input = e.target as HTMLInputElement;
              updateStoredField('sms', 'phone_number', input.value);
            }
          }}
        />
      </om-clickable-element>
    );
  }

  private async handlePhoneNumberSubmit(event: SubmitEvent) {
    event.preventDefault();
    state.showErrors = false;
    //only run if mode is live or test
    if (state.mode !== 'preview') {
      const number = this.phoneNumberValue;
      const dialCode = countries?.find(country => country.code === this.countryCode)?.dial_code;
      const re = /^\+?[1-9]\d{4,14}$/;
      if (!re.test(`${dialCode}${number}`)) {
        // show error message
        this.phoneNumberErrorMsg = 'Please enter a valid phone number';
        return;
      }

      if (state.customFieldErrorMsg && Object.keys(state.customFieldErrorMsg)?.length) {
        state.showErrors = true;
        return;
      }

      if (state.mode === 'test') {
        if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
          clearFormStorage();
        }

        transitionScreen('success');
        return;
      }

      if (state.mode === 'live') {
        const form = new FormData((event?.submitter as HTMLFormElement)?.form);
        if (state.emailValue) form.append('ue', state.emailValue);
        form.append('phone_number_country', this.countryCode);
        form.append('channels.sms.allow_marketing', 'true');

        this.phoneNumberErrorMsg = '';
        try {
          const response = await fetch(SIGNUP_URL, {
            body: form,
            method: 'post',
          });

          if (!response.ok) {
            // Response is not ok, handle the error
            const errorData = await response.json();
            throw errorData;
          }

          await response.json();
          let phoneNumber = form.get('phone_number');
          if (dialCode?.length) {
            phoneNumber = `${dialCode}${phoneNumber}`;
          }

          this.submitEventHandler(phoneNumber as string, state.emailValue && state.emailValue);

          if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
            clearFormStorage();
          }

          transitionScreen('success');
        } catch (err) {
          if (err?.errors && Object.keys(err.errors).length) {
            state.customFieldErrorMsg = {
              ...state.customFieldErrorMsg,
              ...err.errors,
            };
            delete state.customFieldErrorMsg?.phone_number;
            state.showErrors = true;
          }
          this.phoneNumberErrorMsg = err?.errors?.phone_number ?? '';
        }
      }
    }
  }

  componentDidRender() {
    // Only run initialization logic on first render
    if (!this.hasInitialized) {
      this.hasInitialized = true;

      // Then focus the phone input
      if (state.mode !== 'preview' && this.numberInput) {
        setTimeout(() => {
          this.numberInput?.focus();
        }, 100);
      }
    }
  }

  render() {
    const { offset } = getViewportValue(this.screenValues.input);
    const [isAbsolute, offsetVal] = offset ?? [false, '0'];

    // Get stored phone number and country code for restoration (only if mini overlay is enabled)
    let storedPhoneNumber = '';
    if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
      const storedData = getStoredFormData('sms');
      if (storedData) {
        if (storedData.phone_number) {
          storedPhoneNumber = storedData.phone_number;
          if (!this.phoneNumberValue) {
            this.phoneNumberValue = storedPhoneNumber;
          }
        }
        if (storedData.country_code) {
          this.countryCode = storedData.country_code;
        }
      }
    }

    return (
      <Host
        style={{
          ['--textBlockBgColour']: getViewportValue(this.screenValues.textBlockBgColour, 'colour'),
          ['--emailInputOffset']: `${offsetVal ?? '0'}px`,
        }}
      >
        <div
          class={state.isMobileViewport ? 'screen-container is-mobile-viewport' : 'screen-container'}
          onClick={this.layoutContainerClicked.emit}
          ref={(el: HTMLDivElement) => {
            state.currentScreenContainer = el as HTMLElement;
          }}
        >
          <div class="content-area">
            <div class={`content-inner${isAbsolute ? ' absolute-input' : ''}`}>
              <div class="copy-container">
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.title)}
                  class="title"
                  element={{ field: 'title' }}
                />
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.body)}
                  class="body"
                  element={{ field: 'body' }}
                />
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.privacyPolicy)}
                  class="privacy-policy-container"
                  element={{ field: 'privacyPolicy' }}
                />
              </div>
              <div class="bottom-section">
                <form
                  onSubmit={(e: SubmitEvent) => {
                    this.handlePhoneNumberSubmit(e);
                  }}
                  class="form"
                >
                  <input type="hidden" name="__overlay_id" value={state.overlayId} />
                  <input type="hidden" name="number" autocomplete="off" />
                  <div class="hide">
                    <input type="number" name="__number" autocomplete="off" />
                  </div>
                  <input type="hidden" name="@account" value={state.account} />
                  <div class={`inputs-container ${this.screenValues.customFields?.length ? 'has-custom-fields' : ''}`}>
                    {this.phoneNumberInput()}
                    {!!this.screenValues.customFields?.length && (
                      <om-custom-fields customFields={this.screenValues.customFields} screen="sms" />
                    )}

                    <om-clickable-element class="cta-wrapper" element={{ field: 'cta' }}>
                      <button
                        type="submit"
                        class="cta"
                        innerHTML={getViewportValue(this.screenValues.cta, 'text')}
                      ></button>
                    </om-clickable-element>
                  </div>
                  <div id="om-overlays-error-message" class={this.phoneNumberErrorMsg?.length ? 'error' : 'hide'}>
                    {this.phoneNumberErrorMsg}
                  </div>
                </form>
              </div>
            </div>
            <div class="policy-section">
              <om-clickable-element
                element={{ field: 'skipLink' }}
                class="skip-link"
                innerHTML={getViewportValue(this.screenValues.skipLink)}
                onClick={() => {
                  if (state.mode === 'preview') {
                    return;
                  }
                  if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
                    clearFormStorage();
                  }
                  transitionScreen('success');
                }}
              />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
